/**
 * The live-canvas ⇄ module seam (ESAPA-104 / mapd-issues#663).
 *
 * `designSeating` renders seating into a Design's *JSON* at load time. This
 * file is the same idea against the *live* Fabric canvas an admin is looking
 * at: it reads the room off the canvas so the module knows what Tables exist,
 * and it says what the canvas would have to change for it to show what the
 * module holds.
 *
 * Nothing here draws anything and nothing here decides anything about seating.
 * Which Chair an attendee ends up in stays a canvas concern (ADR-0003), and
 * whether a Table has room is the module's answer, never a count of Fabric
 * children — so these functions are pure and structural, and can be covered
 * without Fabric or a DOM.
 */

import { SeatAssignmentModule } from './SeatAssignmentModule';
import { SeatTable } from './types';
import { chairsOf } from '../utils/chairs';

/** A Table on the canvas, as this file needs to see it. */
export interface CanvasTableLike {
  isTable?: boolean;
  tableData?: {
    tableId?: string;
    tableNumber?: unknown;
    tableName?: unknown;
    [key: string]: unknown;
  };
  getObjects?: () => any[];
}

/** A Ticket drawn into a Chair at a Table. */
export interface SeatedOnCanvas {
  ticketId: string;
  /** The key the canvas removes a seated attendee by. */
  uniqueId: string;
}

/** What the canvas would have to change to show what the module holds. */
export interface CanvasSeatingDiff {
  /** Tickets the module seats at a Table that the canvas does not draw there. */
  seat: Array<{ tableId: string; ticketId: string }>;
  /** Tickets the canvas draws at a Table that the module does not seat there. */
  unseat: Array<{ tableId: string; ticketId: string; uniqueId: string }>;
}

/** The Tables among a set of canvas objects. */
export function tableObjectsOn(objects: any[] = []): CanvasTableLike[] {
  return (objects || []).filter((obj: any) => obj?.isTable === true && !!obj?.tableData);
}

/** The Table carrying a given Table Id, or undefined. */
export function findTableObject(objects: any[] = [], tableId: string): CanvasTableLike | undefined {
  if (!tableId) return undefined;
  return tableObjectsOn(objects).find((table) => table.tableData?.tableId === tableId);
}

/**
 * The room the canvas is showing, in the shape the module's model uses. A
 * Table without a Table Id is skipped for the same reason `tablesFromDesign`
 * skips one: the module joins on the Table Id, never on the editable Table
 * Label (ADR-0002).
 */
export function tablesOnCanvas(objects: any[] = []): SeatTable[] {
  const tables: SeatTable[] = [];
  tableObjectsOn(objects).forEach((table) => {
    const data = table.tableData || {};
    const tableId = typeof data.tableId === 'string' ? data.tableId : '';
    if (!tableId) return;
    tables.push({
      tableId,
      label: `${data.tableName ?? data.tableNumber ?? ''}`,
      chairCount: chairsOf(table as any).length,
    });
  });
  return tables;
}

/**
 * The Tickets drawn into Chairs at a Table. Read off the Chairs rather than
 * `tableData.attendees`, because the Chairs are what the admin can see.
 */
export function seatedTicketsOnTable(table: CanvasTableLike | undefined): SeatedOnCanvas[] {
  if (!table) return [];
  const seated: SeatedOnCanvas[] = [];
  chairsOf(table as any).forEach((chair: any) => {
    const ticket = chair?.attendeeData?.assignedTicket;
    const ticketId = ticket?.ticketId;
    if (typeof ticketId !== 'string' || !ticketId) return;
    seated.push({ ticketId, uniqueId: ticket.uniqueId || chair.uniqueId || ticketId });
  });
  return seated;
}

/** The model half of the diff — as little of the module as this needs. */
export type SeatingModel = Pick<SeatAssignmentModule, 'getTicketsAtTable'>;

/**
 * What the canvas would have to change to show what the module holds. A move
 * between two Tables comes back as an unseat at one and a seat at the other,
 * which is what the canvas has to do to draw it; the backend still sees the
 * single write the module made.
 *
 * Only Tables the canvas is showing are considered. A Seat Assignment naming a
 * Table that is not in this room is left alone rather than drawn somewhere.
 */
export function canvasSeatingDiff(objects: any[] = [], model: SeatingModel): CanvasSeatingDiff {
  const diff: CanvasSeatingDiff = { seat: [], unseat: [] };

  tableObjectsOn(objects).forEach((table) => {
    const tableId = table.tableData?.tableId;
    if (typeof tableId !== 'string' || !tableId) return;

    const drawn = seatedTicketsOnTable(table);
    const drawnIds = new Set(drawn.map((s) => s.ticketId));
    const seatedByModel = new Set(model.getTicketsAtTable(tableId));

    drawn.forEach((seat) => {
      if (!seatedByModel.has(seat.ticketId)) {
        diff.unseat.push({ tableId, ticketId: seat.ticketId, uniqueId: seat.uniqueId });
      }
    });
    seatedByModel.forEach((ticketId) => {
      if (!drawnIds.has(ticketId)) diff.seat.push({ tableId, ticketId });
    });
  });

  return diff;
}
